import { TaskPushFeedSchema } from "@herskin/contracts";
import { MarkdownMessage } from "@herskin/markdown";
import { IconButton, InlineError } from "@herskin/ui";
import { IconBellRinging, IconChevronLeft, IconRefresh } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";

function formatTime(value: string) {
  return new Date(value).toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Shanghai",
  });
}

/**
 * 把 Channel Cron 已生成的最终推送内容呈现为只读对话，而不是任务运行卡片。
 * 任务状态、Run ID 与诊断入口属于“任务”工作区，不占用用户查看推送的主流程。
 */
export function TaskPushFeed({
  onBack,
  onUnreadCount,
  agentName,
  agentAvatarUrl,
}: {
  onBack?: () => void;
  onUnreadCount?: (count: number) => void;
  agentName: string;
  agentAvatarUrl: string | null;
}) {
  const [feed, setFeed] = useState<ReturnType<typeof TaskPushFeedSchema.parse> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = TaskPushFeedSchema.parse(await api<unknown>("/api/task-push?limit=100"));
      setFeed(next);
      onUnreadCount?.(next.unreadCount);
      setError("");
      if (next.unreadCount) {
        await api("/api/task-push/read-all", { method: "POST" });
        setFeed({ ...next, unreadCount: 0 });
        onUnreadCount?.(0);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "任务推送读取失败");
    } finally {
      setLoading(false);
    }
  }, [onUnreadCount]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const messages = useMemo(
    () =>
      [...(feed?.records ?? [])]
        .filter(
          (record) =>
            record.taskStatus === "completed" &&
            record.deliveryStatus === "delivery_confirmed" &&
            record.outputContent.trim(),
        )
        .reverse(),
    [feed],
  );

  // 与普通聊天一致：首次进入和收到新推送时定位到最新消息，而不是停在最旧记录。
  useEffect(() => {
    if (!messages.length || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  return (
    <main className="conversation-pane task-push-pane">
      <header className="conversation-header task-push-heading">
        {onBack ? (
          <IconButton
            className="mobile-only mobile-detail-back"
            aria-label="返回会话列表"
            onClick={onBack}
          >
            <IconChevronLeft />
          </IconButton>
        ) : null}
        <div>
          <span className="section-kicker">只读推送会话</span>
          <h1>任务推送</h1>
          <p>这里只显示已取得渠道发送成功凭证的消息。</p>
        </div>
        <IconButton
          aria-label="刷新任务推送"
          title="刷新任务推送"
          disabled={loading}
          onClick={() => void load()}
        >
          <IconRefresh size={17} />
        </IconButton>
      </header>
      {error ? <InlineError title="任务推送读取失败" message={error} /> : null}
      <section ref={scrollRef} className="task-push-scroll" aria-live="polite">
        {!messages.length ? (
          <div className="conversation-empty">
            <div className="empty-orb">
              <IconBellRinging />
            </div>
            <h2>还没有可核验的推送消息</h2>
            <p>任务生成结果不会冒充老板收到的消息；接入渠道出站凭证后才会显示。</p>
          </div>
        ) : (
          <div className="task-push-message-list">
            {messages.map((record) => (
              <article className="task-push-message" key={record.recordId}>
                <header className="task-push-message-author">
                  <span className="task-push-avatar" aria-hidden="true">
                    {agentAvatarUrl ? <img src={agentAvatarUrl} alt="" /> : <IconBellRinging />}
                  </span>
                  <strong>{agentName}</strong>
                </header>
                <div className="task-push-message-bubble">
                  <MarkdownMessage content={record.outputContent} />
                </div>
                <time dateTime={record.completedAt}>{formatTime(record.completedAt)}</time>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
